import React from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import type { Round } from '../../types/developmentDemo';
import { formatTimestamp } from '../../utils/demoStorage';
import { glassPanel } from '../../utils/demoUi';
import { activityForReview, participantName, reviewedRounds } from '../../utils/developmentDemo';
import { LevelBadge } from './DevBadges';
const rounds: Round[] = ['initial', 'reassessment'];

/** Side-by-side view of the initial and reassessment rounds for one participant. */
export function DevProgress({
  participantId
}: {
  participantId: string;
}) {
  const {
    state,
    config
  } = useDevelopmentDemo();
  const reviewed = reviewedRounds(state, participantId);
  const data = rounds.map(round => {
    const ids = reviewed[round];
    return {
      round,
      task: config.tasks[round],
      submission: ids && state.submissions.find(item => item.id === ids.submissionId),
      review: ids && state.reviews.find(item => item.id === ids.reviewId)
    };
  });
  const practice = data[0].review ? activityForReview(state, data[0].review.id) : undefined;
  return <div data-id="element-647" data-mp="11edmu0mxrrbo">
      <p className="mb-5 text-sm leading-relaxed text-ink-muted" data-id="element-648" data-mp="11fon3qh6agy1">
        Showing reviewed rounds for {participantName(config, participantId)}. The follow-up scenario changes the context, so the rounds are
        compared side by side rather than scored. No overall score or automatic claim of improvement is produced.
      </p>
      <div className={`${glassPanel} overflow-x-auto`} data-id="element-649" data-mp="ngysqilqmvdd">
        <table className="w-full min-w-[680px] text-left text-sm" data-id="element-650" data-mp="invzkbkvhnxp">
          <caption className="sr-only" data-id="element-651" data-mp="dp2jcbe7bitu">Initial and reassessment rounds compared</caption>
          <thead data-id="element-652" data-mp="dmq07uwpj2f7">
            <tr className="border-b border-line" data-id="element-653" data-mp="8q81bbo25yas">
              <th scope="col" className="w-48 px-5 py-4 text-xs font-semibold text-ink-muted" data-id="element-654" data-mp="8rj1l1iaonx5">
                Criterion
              </th>
              {data.map(item => <th key={item.round} scope="col" className="px-5 py-4 align-top" data-id="element-655" data-mp="3vvqog19945zi">
                  <span className="text-xs font-semibold text-ink-muted" data-id="element-656" data-mp="3l9aqe4rxqpp3">
                    {item.round === 'initial' ? 'Round 1 · Initial task' : 'Round 2 · Reassessment'}
                  </span>
                  <span className="mt-1 block font-heading text-base font-bold text-ink-dark" data-id="element-657" data-mp="3c9nolhvanhvi">{item.task.title}</span>
                  <span className="mt-1 block text-xs font-normal text-ink-muted" data-id="element-658" data-mp="3h8rvg1uqx3io">
                    {item.submission ? `${item.submission.id} · ${formatTimestamp(item.submission.at)}` : 'Not yet reviewed'}
                  </span>
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-659" data-mp="3v6ynh1d0k9af">
            {config.criteria.map(criterion => <tr key={criterion.id} className="border-b border-line align-top" data-id="element-660" data-mp="40xagn9hn5m5">
                <th scope="row" className="px-5 py-4 font-semibold text-ink-dark" data-id="element-661" data-mp="rrb1qbk910fo">
                  {criterion.label}
                </th>
                {data.map(item => {
              const observation = item.review?.observations.find(entry => entry.criterionId === criterion.id);
              return <td key={item.round} className="px-5 py-4" data-id="element-662" data-mp="11vrfmze647gu">
                      {observation ? <>
                          <LevelBadge level={observation.level} data-id="element-663" data-mp="1fll4vitl4tr3" />
                          <p className="mt-1 leading-relaxed text-ink-muted" data-id="element-664" data-mp="1aob79fw1lbs6">{observation.observation}</p>
                        </> : <span className="text-ink-muted" data-id="element-665" data-mp="iw3r0gkg81eb">—</span>}
                    </td>;
            })}
              </tr>)}
            <tr className="border-b border-line align-top" data-id="element-666" data-mp="wtsb3m6oz4ib">
              <th scope="row" className="px-5 py-4 font-semibold text-ink-dark" data-id="element-667" data-mp="rvqs1199n56u">
                Response
              </th>
              {data.map(item => <td key={item.round} className="px-5 py-4 whitespace-pre-wrap leading-relaxed text-ink-dark" data-id="element-668" data-mp="23bxj1bffbmq4">
                  {item.submission?.response ?? <span className="text-ink-muted" data-id="element-669" data-mp="cw54llzuhs4t">—</span>}
                </td>)}
            </tr>
            <tr className="align-top" data-id="element-670" data-mp="hugd9teelwlg">
              <th scope="row" className="px-5 py-4 font-semibold text-ink-dark" data-id="element-671" data-mp="mt14x4flb7ya">
                Feedback
              </th>
              {data.map(item => <td key={item.round} className="px-5 py-4 leading-relaxed text-ink-dark" data-id="element-672" data-mp="qw61yrgwihfj">
                  {item.review?.feedback ?? <span className="text-ink-muted" data-id="element-673" data-mp="vrlftjewsdby">—</span>}
                </td>)}
            </tr>
          </tbody>
        </table>
      </div>

      <section aria-labelledby="practice-between" className="mt-6 rounded-xl border border-line bg-white/80 p-5" data-id="element-674" data-mp="15kk4t2ebi5w3">
        <h3 id="practice-between" className="font-heading font-bold text-ink-dark" data-id="element-675" data-mp="1ahanlxhibfl7">
          Practice between rounds
        </h3>
        {practice ? <>
            <p className="mt-1 text-sm text-ink-muted" data-id="element-676" data-mp="1jiii04gwjwa0">
              {practice.id} · {practice.title} · {practice.status === 'completed' ? 'Completed' : 'In progress'}
            </p>
            {practice.reflection && <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-ink-dark" data-id="element-677" data-mp="2z16n5leqw21u">Reflection: {practice.reflection}</p>}
          </> : <p className="mt-1 text-sm text-ink-muted" data-id="element-678" data-mp="2v2hc3zh6iwld">No development activity has been assigned yet.</p>}
      </section>
    </div>;
}